import { getMmuSkuList } from 'api/mall';
import SkuInventory from 'comps/SkuInventory';
import Stepper from 'comps/Stepper';
import useProductDetail from 'hooks/useProductDetail';
import useUrl from 'hooks/useUrl';
import { numberDivide } from 'libs/compute';
import { MAX_QUANTITY, ProductBuyStatus } from 'libs/cons';
import { LocContext, PriceContext, UserContext } from 'libs/contexts';
import { NetPriceSourceEnum, QuoteMode } from 'libs/enums';
import { PageMallSkuDetail, ProductBuyStatusEnum } from 'libs/types';
import { clone, isEmpty } from 'rambdax';
import { forwardRef, useContext, useEffect, useImperativeHandle, useState } from 'react';
import { Button, Popup, Toast, Collapse } from 'react-vant';
import MmuSkuListPopupWithIcon from './MmuSkuListPopupWithIcon';
import NetPriceDetailPopupWithIcon from '@/comps/NetPriceDetailPopupWithIcon';
import ProductDetailAttribute from './ProductDetailAttribute';
import ProductDetailSalePrice from './ProductDetailSalePrice';

type ProductDetailMmuPopupProps = {
  skuList: Array<MmuMallSku>;
  setShowUsePart: (showUsePart: boolean) => void;
  showUsePart: boolean;
  selectTradeAgreement: TradeAgreementInfo;
  shopInfo: Record<string, string>;
  skuDetail: PageMallSkuDetail;
  selectAccountPeriod: DiscountByMethodInfo;
  selectStation: Station;
  station: Array<Station>;
  visible: boolean;
  productStatus: ProductBuyStatusEnum;
  mmuAttributeList: SkuCategoryAttributeDTO[];
  mmuAttributeClassifiers: string[];
  source: string;
  setStation: (station: Array<Station>) => void;
  setMmuAttributeList: (mmuAttributeList: SkuCategoryAttributeDTO[]) => void;
  setMmuAttributeClassifiers: (mmuAttributeClassifiers: string[]) => void;
  setSelectStation: (station: Station) => void;
  setSkuList: (skuList: Array<MmuMallSku>) => void;
  onClose: () => void;
  onChangeMmu: (mmuAttributeList) => void;
  onChangeSku: (sysNo: string) => void;
  children?: React.ReactNode;
  isBuyable?: boolean;
  isOutSide?: boolean;
};

export type SkuCategoryAttributeDTO = SkuDetail.SkuCategoryAttributeDTO;

function ProductDetailMmuPopup(
  {
    visible,
    productStatus,
    skuList,
    source,
    setShowUsePart,
    showUsePart,
    selectTradeAgreement,
    shopInfo,
    skuDetail,
    mmuAttributeTagList,
    mmuAttributeList,
    mmuAttributeClassifiers,
    selectAccountPeriod,
    selectStation,
    station,
    setSelectStation,
    setSkuList,
    setStation,
    setMmuAttributeList,
    setMmuAttributeClassifiers,
    onClose,
    onChangeMmu,
    onChangeSku,
    children,
    isBuyable,
    isOutSide,
  }: ProductDetailMmuPopupProps,
  ref,
) {
  const userInfo = useContext(UserContext);
  const [, setManifestEnable] = useState(true);
  const { getTotalPrice } = useProductDetail();
  const [quantity, setQuantity] = useState(getInitQuantity());
  const { showPrice, tip } = useContext(PriceContext);

  const skuForPriceCalculate = selectTradeAgreement?.skuForPriceCalculate?.[0];
  const netPriceSourceInformation =
    skuForPriceCalculate?.agreementPriceForm?.netPriceSourceInformation;

  const { location } = useContext(LocContext);
  const [attributeList, setAttributeList] = useState<SkuCategoryAttributeDTO[]>([]);
  const { getPublicUrl } = useUrl();
  useEffect(() => {
    setQuantity(getInitQuantity());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skuDetail.sysNo]);

  useImperativeHandle(ref, () => ({
    buildSku,
    getSkuList,
  }));

  // useEffect(() => {
  //   const _attributeList = clone(skuDetail.mmuAttributeList || []);
  //   const uniqueClassifiers = _attributeList.reduce((acc, current) => {
  //     if (!acc.some((item) => item === current.attributeTag)) {
  //       acc.push(current.attributeTag);
  //     }
  //     return acc;
  //   }, []);
  //   const _classifiers = uniqueClassifiers.filter((t) => !!t);
  //   const classifiers =
  //     _classifiers.length < uniqueClassifiers.length ? _classifiers.concat(null) : _classifiers;
  //   console.log('classifiers: ', classifiers);
  //   setMmuAttributeClassifiers(classifiers);
  //   setMmuAttributeList(_attributeList);
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [skuDetail.mmuAttributeList]);

  useEffect(() => {
    const mmuAttributeTagList = skuDetail?.mmuAttributeTagList?.flatMap(
      (mmuAttributeListWithTag) => {
        return mmuAttributeListWithTag.mmuAttributeList;
      },
    );
    const _attributeList = clone(mmuAttributeTagList || []);
    // const uniqueClassifiers = _attributeList.reduce((acc, current) => {
    //   if (!acc.some((item) => item === current.attributeTag)) {
    //     acc.push(current.attributeTag);
    //   }
    //   return acc;
    // }, []);
    // const _classifiers = uniqueClassifiers.filter((t) => !!t);
    // const classifiers =
    //   _classifiers.length < uniqueClassifiers.length ? _classifiers.concat(null) : _classifiers;
    // console.log('classifiers: ', classifiers);
    // setMmuAttributeClassifiers(classifiers);
    setMmuAttributeList(_attributeList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skuDetail.mmuAttributeTagList]);

  useEffect(() => {
    setAttributeList(clone(skuDetail.attributeList || []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skuDetail.attributeList]);

  useEffect(() => {
    if (location.sysNo && skuList?.length) {
      getSkuList(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.sysNo]);

  function getInitQuantity() {
    const { buyStep } = skuDetail;
    /**
     * 若步长 <1，且能被1 整除时，按“1”填充
     * 若步长 <1，且不能被1整除时，则按步长填充
     * 若步长 >1，则按步长加减
     */
    if (buyStep) {
      if (buyStep >= 1) {
        return buyStep;
      } else {
        // 不能被1整除
        if (numberDivide(1, buyStep)?.includes('.')) {
          return buyStep;
        } else {
          return 1;
        }
      }
    } else {
      return 1;
    }
  }

  function getSelectAttr() {
    let arr = [];
    attributeList?.forEach((item) => {
      item.categoryAttributeValueList.forEach((attr) => {
        if (attr.active) {
          arr.push(attr.attributeValue);
        }
      });
    });
    mmuAttributeList?.forEach((attribute) => {
      if (!attribute.hasCustomize) {
        attribute.categoryAttributeValueList.forEach((attr) => {
          if (attr.active) {
            arr.push(attr.attributeValue);
          }
        });
      } else {
        const attributeValueItem = attribute.categoryAttributeValueList?.[0];
        const attributeValue = attributeValueItem?.attributeValue;
        if (attributeValue !== undefined && attributeValue !== '') {
          arr.push(attributeValueItem.prefix + attributeValue + attributeValueItem.suffix);
        }
      }
    });
    arr.push(`数量${quantity}`);
    return arr.join('，');
  }

  function handleChangeMmu() {
    const mmuAttributeList = mmuAttributeTagList?.flatMap((mmuAttributeListWithTag) => {
      return mmuAttributeListWithTag.mmuAttributeList;
    });
    setMmuAttributeList([...mmuAttributeList]);
    onChangeMmu([...mmuAttributeList]);
  }

  function handleChangeAttr({ sysNo }) {
    setAttributeList([...attributeList]);
    onChangeSku(sysNo);
  }

  function addSkuList() {
    // 未登录
    if (!userInfo) {
      window.location.href = `/login?return_url=${encodeURIComponent(window.location.href)}`;
      return;
    }

    if (quantity <= 0) {
      Toast.fail('请输入采购数量！');
      return;
    }
    getSkuList(true);
  }

  function buildSku() {
    const mmuAttributeList = mmuAttributeTagList?.flatMap((mmuAttributeListWithTag) => {
      return mmuAttributeListWithTag.mmuAttributeList;
    });
    
    let reqMmuAttributeList = clone(mmuAttributeList || []);
    // 是否有必填的错误提示
    let requireErrMsg = '';
    const buildSkuDetail: SkuDetail.PageMallSkuDetail = clone(skuDetail);
    const commentArr = [];
    const attributeList = buildSkuDetail.attributeList.filter((attribute) => {
      attribute.categoryAttributeValueList = attribute.categoryAttributeValueList.filter(
        (categoryAttribute) => categoryAttribute.active,
      );
      return !!attribute.categoryAttributeValueList?.length;
    });
    reqMmuAttributeList = reqMmuAttributeList?.map((attribute) => {
      if (!attribute.hasCustomize) {
        attribute.categoryAttributeValueList = attribute.categoryAttributeValueList.filter(
          (categoryAttribute) => {
            if (categoryAttribute.active) {
              commentArr.push(categoryAttribute.attributeValue);
            }
            return categoryAttribute.active;
          },
        );
      } else {
        const categoryAttributeValueItem =
          attribute?.categoryAttributeValueList?.length && attribute.categoryAttributeValueList[0];
        const { attributeValue } = categoryAttributeValueItem || {};
        if (attributeValue !== undefined && attributeValue !== '') {
          categoryAttributeValueItem.active = true;
          commentArr.push(
            categoryAttributeValueItem.prefix +
              categoryAttributeValueItem.attributeValue +
              categoryAttributeValueItem.suffix,
          );
        } else {
          if (attribute.hasRequired && !requireErrMsg) {
            requireErrMsg = attribute.attributeName + '不能为空';
          }
        }
      }
      return attribute;
    });

    if (requireErrMsg) {
      Toast(requireErrMsg);
      return false;
    }
    console.log('attributeList', attributeList);
    return {
      sysNo: buildSkuDetail.sysNo,
      name: buildSkuDetail.name,
      brandSysNo: buildSkuDetail.brandSysNo,
      brandName: buildSkuDetail.brandName,
      salesPrice: buildSkuDetail.salesPrice,
      excludeTaxPrice: buildSkuDetail.excludeTaxPrice,
      netPriceWithTax: buildSkuDetail.netPriceWithTax,
      netPriceWithoutTax: buildSkuDetail.netPriceWithoutTax,
      netPriceSource: buildSkuDetail.netPriceSource,
      priceWithTax: buildSkuDetail.priceWithTax,
      priceWithOutTax: buildSkuDetail.priceWithOutTax,
      quoteMode: buildSkuDetail.quoteMode,
      shopQuantity: quantity,
      usePart: buildSkuDetail.usePart,
      comment: commentArr.join('，'),
      attributeList: attributeList,
      mmuAttributeList: reqMmuAttributeList,
      taxRate: buildSkuDetail.taxRate,
      unitName: buildSkuDetail.unitName,
      unitSysNo: buildSkuDetail.unitSysNo,
      mainImage: buildSkuDetail.mainImage,
      buyStep: buildSkuDetail.buyStep || 1,
      concreteFlag: buildSkuDetail.concreteFlag,
    };
  }

  async function getSkuList(addSku) {
    let sku;
    if (addSku) {
      sku = buildSku();
      if (!sku) return;
    }
    const mallSkuList = addSku ? [...skuList, sku] : skuList;
    if (isEmpty(mallSkuList)) return;
    const json = {
      agreementSysNo: selectTradeAgreement?.sysNo,
      accountPeriod: selectAccountPeriod?.accountPeriod,
      billingPeriod: selectAccountPeriod?.billingPeriod,
      areaSysNo: location.sysNo,
      supplierSysNo: skuDetail.supplierSysNo,
      mallSkuList: mallSkuList,
    };
    setManifestEnable(false);
    const res = await getMmuSkuList(json);
    setManifestEnable(true);
    if (res.code === 200) {
      const { mallSkuList, showUsePart } = res.data;
      setShowUsePart(showUsePart);
      const list = mallSkuList.map((sku) => {
        let mapperSku = json.mallSkuList.find((item) => item.sysNo === sku.sysNo);
        if (mapperSku) {
          sku.concreteFlag = mapperSku.concreteFlag;
        }

        sku.priceWithOutTaxHasInput = false;
        sku.priceWithTaxHasInput = false;
        sku.mallSkuSysNo = sku.sysNo;
        // 没有网价
        const NoNetPrice = sku.netPriceWithoutTax == null && sku.netPriceWithTax == null;
        // 由采购方自行填写价格
        const purchaserCustomizePrice =
          sku.skuForPriceCalculate && sku.skuForPriceCalculate.purchaserCustomizePrice;
        const noUnitConversionRate =
          sku.skuForPriceCalculate && sku.skuForPriceCalculate.noUnitConversionRate;
        if (
          sku.netPriceSource != null &&
          (NoNetPrice || purchaserCustomizePrice || noUnitConversionRate)
        ) {
          if (sku.quoteMode === QuoteMode.WITHOUT_TAX_TYPE) {
            sku.priceWithOutTaxHasInput = true;
          } else {
            sku.priceWithTaxHasInput = true;
          }
        }
        return sku;
      });
      setSkuList(list);
      setStation(res.data.station);
    }
  }

  const selectAttr = getSelectAttr();
  return (
    <>
      <Popup
        visible={visible}
        position="bottom"
        closeable
        onClose={onClose}
        transition="simple-bottom-transition"
      >
        <div className="detailPopup">
          <div className="cartInfo">
            <img src={getPublicUrl(skuDetail.mainImage as string)} alt="" />
            <div className="info-wrap">
              {productStatus === 'NotInArea' ? (
                <div className="noProduct">{ProductBuyStatus[productStatus]}</div>
              ) : null}

              {showPrice && (!isOutSide || isBuyable) ? (
                <div className="afterPrice">
                  <ProductDetailSalePrice
                    skuDetail={skuDetail}
                    selectTradeAgreement={selectTradeAgreement}
                    productStatus={productStatus}
                  />
                </div>
              ) : (
                <div className="afterPrice">¥ {tip}</div>
              )}
              <SkuInventory
                inventory={skuDetail.inventory}
                unitName={skuDetail.unitName}
                showInventory={skuDetail.showInventory}
              />
              {skuForPriceCalculate?.netPriceSource &&
                skuForPriceCalculate?.netPriceSource !== NetPriceSourceEnum.BASE_PRICE && (
                  <p className="price-tip">
                    {skuForPriceCalculate?.netPriceSource === NetPriceSourceEnum.INFO
                      ? '信息价：'
                      : skuForPriceCalculate?.netPriceSource === NetPriceSourceEnum.BASE
                      ? '基础价：'
                      : '网价：'}
                    {netPriceSourceInformation?.price == null ? (
                      '--'
                    ) : (
                      <>
                        <span className="price-tip-txt">
                          {netPriceSourceInformation.price + netPriceSourceInformation.suffix}
                        </span>
                        <NetPriceDetailPopupWithIcon
                          skuSysNo={skuDetail?.sysNo}
                          tradeAgreementSysNo={selectTradeAgreement?.sysNo}
                          netPriceSource={
                            skuForPriceCalculate?.agreementPriceForm?.netPriceSourceInformation
                          }
                        />
                      </>
                    )}
                  </p>
                )}

              <div className="choosen">已选：{selectAttr}</div>
            </div>
          </div>
          <div className="cartContent">
            {attributeList.map((attribute, attributeIndex) => (
              <ProductDetailAttribute
                isMmu={false}
                attribute={attribute}
                key={attributeIndex}
                onChange={handleChangeAttr}
              />
            ))}
            {/* {mmuAttributeClassifiers?.map((attributeTag) => (
              <>
                {attributeTag && <div className="attributeTag">{attributeTag}</div>}
                {mmuAttributeList
                  .filter((t) => t.attributeTag === attributeTag)
                  .map((attribute, attributeIndex) => (
                    <ProductDetailAttribute
                      isMmu={true}
                      attribute={attribute}
                      key={attributeIndex}
                      onChange={handleChangeMmu}
                    />
                  ))}
              </>
            ))} */}
            {mmuAttributeTagList?.map((mmuAttributeListWithTag) => {
              const { tagName, mmuAttributeList } = mmuAttributeListWithTag;
              if (tagName) {
                return (
                  <Collapse key={tagName} border={false}>
                    <Collapse.Item title={tagName} border={false}>
                      {mmuAttributeList.map((attribute) => (
                        <ProductDetailAttribute
                          isMmu={true}
                          attribute={attribute}
                          key={tagName}
                          onChange={handleChangeMmu}
                        />
                      ))}
                    </Collapse.Item>
                  </Collapse>
                );
              } else {
                return mmuAttributeList.map((attribute, attributeIndex) => (
                  <ProductDetailAttribute
                    isMmu={true}
                    attribute={attribute}
                    key={attributeIndex}
                    onChange={handleChangeMmu}
                  />
                ));
              }
            })}
            <div className="num-wrap">
              <div className="optionName">数量</div>
              <div className="optionVals stepper">
                <Stepper
                  value={quantity}
                  onChange={(val) => {
                    setQuantity(val as number);
                  }}
                  step={skuDetail.buyStep ?? 1}
                  min={skuDetail.buyStart ?? 1}
                  max={
                    skuDetail.inventory === null || skuDetail.inventory === undefined
                      ? MAX_QUANTITY
                      : skuDetail.inventory
                  }
                  disabled={!isBuyable}
                  decimalLength={4}
                  integer={false}
                  autoFix
                  inputWidth="70px"
                  defaultValue={skuDetail.buyStart}
                  onExceed={() => {
                    if (!!skuDetail.inventory) {
                      Toast.info(`${skuDetail.name}库存量为${skuDetail.inventory}，请勿超量购买`);
                    }
                  }}
                />
              </div>
            </div>
          </div>
          {children ? (
            children
          ) : (
            <div className="cartBtn">
              {isBuyable ? (
                <>
                  <div className="cart-btn-left">
                    <MmuSkuListPopupWithIcon
                      source={source}
                      showUsePart={showUsePart}
                      skuList={skuList}
                      selectTradeAgreement={selectTradeAgreement}
                      shopInfo={shopInfo}
                      skuDetail={skuDetail}
                      selectAccountPeriod={selectAccountPeriod}
                      selectStation={selectStation}
                      station={station}
                      setSelectStation={setSelectStation}
                      setSkuList={setSkuList}
                    />
                    {getTotalPrice(skuList) ? (
                      <span className="footer-price-wrap">
                        <span className="footer-price-icon">￥</span>
                        {getTotalPrice(skuList)}
                      </span>
                    ) : null}
                  </div>

                  <Button
                    round
                    text="加入清单"
                    type="primary"
                    style={{ width: '110px', fontSize: '16px' }}
                    // 不展示价格并且有用户信息，说明当前是仅供应商身份导致价格不可见，这个时候按钮要禁用
                    disabled={!showPrice && !!userInfo}
                    onClick={addSkuList}
                  />
                </>
              ) : (
                <Button
                  text={ProductBuyStatus[productStatus]}
                  type="primary"
                  round
                  style={{ width: '110px', fontSize: '16px', flex: 1 }}
                  disabled
                />
              )}
            </div>
          )}
        </div>
      </Popup>
      <style jsx>{`
        .detailPopup {
          max-height: 555px;
          background-color: #fff;
          position: relative;
          overflow: auto;
        }

        .cartInfo {
          display: flex;
          background: #fff;
          padding: 20px 15px;
          align-items: start;
        }

        .cartInfo img {
          height: 86px;
          width: 86px;
          margin-right: 20px;
        }

        .choosen {
          font-size: 12px;
          color: #999999;
          overflow: hidden;
          text-overflow: ellipsis;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
        }

        .attributeTag {
          margin-top: 30px;
          margin-bottom: -10px;
        }

        .optionName {
          font-size: 13px;
          color: #2e2d37;
          text-align: justify;
          line-height: 14px;
          margin-top: 10px;
        }

        .optionVals {
          display: flex;
          flex-wrap: wrap;
          margin: 17px 0 0;
        }

        .cartContent {
          padding: 0 16px;
          max-height: 429px;
          overflow: auto;
          padding-bottom: 75px;
        }

        .info-wrap {
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          min-height: 86px;
          padding-top: 3px;
        }

        .afterPrice {
          font-size: 15px;
          color: #ff6602;
          line-height: 18px;
        }

        .num-wrap {
          margin-top: 20px;
        }
        .price-tip {
          font-size: 12px;
          color: #333333;
          display: flex;
          align-items: center;
          margin-bottom: 0;
        }

        .cartBtn {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          background: #fff;
          padding: 6px 15px 6px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .cart-btn-left {
          display: flex;
          align-items: center;
        }
        .footer-price-wrap {
          font-size: 16px;
          color: #ea5604;
          font-weight: bold;
          margin-left: 15px;
          word-break: break-word;
          flex: 1;
          line-height: 18px;
        }
        .footer-price-icon {
          font-size: 14px;
        }
        .price-tip-txt {
          margin-right: 6px;
        }

        .stepper :global(.rv-stepper__input) {
          font-size: 12px;
        }
      `}</style>

      <style jsx global>{`
        .rv-collapse-item__content .item-wrap {
          margin: 6px 0 !important;
        }

        .rv-cell.rv-collapse-item__title {
          padding: 0 !important;
          margin: 10px 0 !important;
        }
      `}</style>
    </>
  );
}

export default forwardRef(ProductDetailMmuPopup);
